import React from 'react';
import { CardVariants, Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import MetricCard from '../../common/metricCard/MetricCard';
import KpiDelta from './KpiDelta';
import styles from './styles/billing-kpi-card.module.scss';

/**
 * Single KPI tile for the Billing Hub page (Figma 6085:9006). Composes
 * the design-system `MetricCard` and adapts its inner content to the
 * Figma's icon-on-top + label/value + optional delta layout.
 *
 * The `variant` prop is forwarded to the underlying `Card`; the
 * highlight variant (navy gradient + white text) automatically swaps
 * the icon chip, label, value, and delta colors for legibility.
 *
 * Props:
 *  - `variant`: `CardVariants.HIGHLIGHT` for the hero tile, otherwise omit.
 *  - `IconComponent`: heroicons (or any) component rendered inside the
 *                     leading chip; the card owns its sizing/color.
 *  - `label`: small caption above the value.
 *  - `value`: large headline number/text.
 *  - `delta`: optional `{ change, tooltip }` — when provided, renders a
 *             `KpiDelta` row beneath the value.
 */
function BillingKpiCard({ variant, IconComponent, label, value, delta }) {
    const isHighlight = variant === CardVariants.HIGHLIGHT;
    const iconWrapperClassName = isHighlight ? styles.iconBoxHighlight : styles.iconBoxReset;
    const iconClassName = isHighlight ? styles.iconHighlight : styles.iconBadge;

    return (
        <MetricCard
            variant={variant}
            image={IconComponent ? <IconComponent className={iconClassName} /> : null}
            classNames={{ icon: iconWrapperClassName }}
            className={styles.card}
        >
            <div className={styles.body}>
                <Text
                    as={TextAs.SPAN}
                    size={TextSizes.SMALL}
                    className={`${styles.label} ${isHighlight ? styles.labelHighlight : ''}`}
                >
                    {label}
                </Text>
                <Text
                    as={TextAs.SPAN}
                    size={TextSizes.DISPLAY_SM}
                    className={`${styles.value} ${isHighlight ? styles.valueHighlight : ''}`}
                >
                    {value}
                </Text>
                {delta ? (
                    <KpiDelta
                        change={delta.change}
                        tooltip={delta.tooltip}
                        tone={isHighlight ? 'highlight' : 'default'}
                    />
                ) : null}
            </div>
        </MetricCard>
    );
}

export default BillingKpiCard;
